import { observer } from 'mobx-react';
import React, { FC, useEffect, useState } from 'react';
import { runInAction } from 'mobx';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Card } from '@monorepo/base/src/components/card/card';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Textarea } from '@monorepo/base/src/components/form/textarea/textarea';
import { IAdminxStore } from '../../../../stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import styles from './basic-info.module.scss';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { ButtonDimensions, PrimaryButton as BadIspApplyButton } from '@monorepo/base/src/components/buttons/buttons';
import { BidderApi } from '../../../../apis/bidder.api';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { normalizeBadIspText, sanitizeBadIspWordsDraft } from '../../../../utils/bad-isp-words';

interface BasicInfoProps {
	isEditPage: boolean;
}

export const BasicInfo: FC<BasicInfoProps> = observer(props => {
	const { bidderAdvertiserStore, modalsStore } = useStores<IAdminxStore>();
	const { dispatchLog } = useLogs();
	const isEdit = props.isEditPage;
	const [isSavingBadIsp, setIsSavingBadIsp] = useState(false);
	const advertiserCrudStore = bidderAdvertiserStore.getCrud();
	const formStore = advertiserCrudStore.getFormStore();
	const advertiser = advertiserCrudStore.getData().advertiser;
	const originAdvertiser = advertiserCrudStore.getOriginData().advertiser;
	if (advertiser === undefined) {
		return null;
	}
	const [editCurrBalance, setEditCurrBalance] = useState(advertiser.balance);

	const isBadIspDirty =
		isEdit &&
		!!originAdvertiser &&
		!!advertiser.advertiser_id &&
		normalizeBadIspText(advertiser.bad_isp_words) !== normalizeBadIspText(originAdvertiser.bad_isp_words);

	useEffect(() => {
		if (isEdit) {
			setEditCurrBalance(advertiser.balance);
		}
	}, [isEdit, advertiser.balance]);

	useEffect(() => {
		const raw = advertiser.bad_isp_words ?? '';
		const draft = sanitizeBadIspWordsDraft(raw);
		if (draft !== raw) {
			advertiser.bad_isp_words = draft || undefined;
		}
	}, [advertiser.advertiser_id, advertiser.bad_isp_words]);

	return (
		<Card
			id={'basicInfo'}
			title={'Basic Info'}
			icon={'settings-01'}
			className={styles.wrapper}
			debugProps={{ dataAttrs: [new DataAttribute('id', 'basic_info_section')] }}>
			<Card>
				<div className={styles.inputsWrapper}>
					<Input
						className={styles.advertiserName}
						value={advertiser.advertiser_name}
						placeholder={'Advertiser Name'}
						onValue={value => {
							advertiser.advertiser_name = value;
							formStore.clearError('name');
						}}
						required
						error={formStore.getErrors().get('name')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'name')] }}
					/>
					<Input
						className={styles.advertiserId}
						value={advertiser.advertiser_id?.toString()}
						placeholder={'Advertiser ID'}
						disabled
						error={formStore.getErrors().get('name')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'name')] }}
					/>
				</div>
				{!isEdit && (
					<Input
						value={advertiser.total_budget?.toString()}
						placeholder={'Total Budget'}
						type={'number'}
						required
						onValue={value => {
							advertiser.total_budget = parseFloat(value ?? 0);
							formStore.clearError('totalBudget');
						}}
					/>
				)}
				{isEdit && (
					<Input
						value={editCurrBalance?.toString()}
						placeholder={'Balance'}
						type={'number'}
						required
						onValue={value => {
							setEditCurrBalance(parseFloat(value ?? 0));
							advertiser.newBalance = parseFloat(value ?? 0);
							formStore.clearError('balance');
						}}
						error={formStore.getErrors().get('balance')?.getMsg()}
					/>
				)}
				<Input
					value={advertiser.priority?.toString()}
					placeholder={'Priority'}
					type={'number'}
					required
					onValue={value => {
						advertiser.priority = parseInt(value ?? 0);
						formStore.clearError('priority');
					}}
				/>
				<div className={styles.dropdownsWrapper}>
					<DropdownNew
						className={styles.dropwdowns}
						defaultValues={advertiser.should_receive_l1?.toString() ? [advertiser.should_receive_l1?.toString()] : []}
						label="Should Receive L1"
						required
						options={[
							{ label: 'True', value: 'true' },
							{ label: 'False', value: 'false' },
						]}
						onSelect={v => {
							advertiser.should_receive_l1 = v[0] === 'true';
							formStore.clearError('should_receive_l1');
						}}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'should_receive_l1')] }}
					/>
					<DropdownNew
						className={styles.dropwdowns}
						defaultValues={
							advertiser.use_enhanced_black_list?.toString() ? [advertiser.use_enhanced_black_list?.toString()] : []
						}
						label="Use Enhanced Black List"
						required
						options={[
							{ label: 'True', value: 'true' },
							{ label: 'False', value: 'false' },
						]}
						onSelect={v => {
							advertiser.use_enhanced_black_list = v[0] === 'true';
							formStore.clearError('use_enhanced_black_list');
						}}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'use_enhanced_black_list')] }}
					/>
				</div>

				<div id="badIspWords">
					<Textarea
						value={advertiser.bad_isp_words ?? ''}
						placeholder={'Bad ISP words'}
						rows={4}
						onValue={value => {
							advertiser.bad_isp_words = sanitizeBadIspWordsDraft(value) || undefined;
						}}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'bad_isp_words')] }}
					/>
					{isBadIspDirty ? (
						<React.Fragment>
							<Spacer />
							<div className={styles.applyBadIspButtonRow}>
								<BadIspApplyButton
									className={styles.applyBadIspButton}
									dimension={ButtonDimensions.Small}
									disabled={isSavingBadIsp}
									isRotateIcon={isSavingBadIsp}
									icon={isSavingBadIsp ? 'loading-01' : undefined}
									onClick={async () => {
										if (!advertiser.advertiser_id) {
											return;
										}
										const bad_isp_words = normalizeBadIspText(advertiser.bad_isp_words);
										setIsSavingBadIsp(true);
										try {
											await BidderApi.setBadIspWordsPerAdvertiser({
												advertiser_bad_isp_words: {
													advertiser_id: advertiser.advertiser_id,
													bad_isp_words,
												},
											});
											runInAction(() => {
												const d = advertiserCrudStore.getData().advertiser;
												const o = advertiserCrudStore.getOriginData().advertiser;
												const next = bad_isp_words.length ? bad_isp_words : undefined;
												if (d) {
													d.bad_isp_words = next;
												}
												if (o) {
													o.bad_isp_words = next;
												}
											});
											dispatchLog({
												msg: 'Bad ISP words updated',
												type: DispatchLogsTypes.Success,
											});
										} catch (e: any) {
											modalsStore.addModal({
												children: (
													<ActionModal type={ModalTypes.Error} primaryBtnTitle={'OK'}>
														<div>
															<h2>Failed to update bad ISP words</h2>
															{e.data?.errors?.map((err: { field: string; message: string }) => (
																<div key={err.field}>{`${err.field}: ${err.message}`}</div>
															))}
														</div>
													</ActionModal>
												),
											});
										} finally {
											setIsSavingBadIsp(false);
										}
									}}>
									Apply bad ISP words
								</BadIspApplyButton>
							</div>
						</React.Fragment>
					) : null}
				</div>

				<Spacer />
			</Card>
		</Card>
	);
});
